import React, { useMemo } from 'react'

import { calculatePercentage } from '~/helpers'
import type { PacingStatus } from '~/modules/retailMedia/services/campaign/spend-reports'

import * as S from './styles'

export type BudgetPacingProgressBarProps = {
  value: number
  goal: number
  pacing: PacingStatus
  /** When set, only the track is shown (no header / indicator row). */
  variant?: 'default' | 'compact'
  children?: React.ReactNode
} & React.HTMLAttributes<HTMLDivElement>

const PacingGlyph = ({ pacing }: { pacing: PacingStatus }) => {
  if (pacing === 'on_track') {
    return <S.BadgeDot aria-hidden="true" />
  }
  const isDown = pacing === 'underpacing' || pacing === 'critical_underpacing'
  return isDown ? (
    <S.BadgeArrowDown aria-hidden="true" />
  ) : (
    <S.BadgeArrowUp aria-hidden="true" />
  )
}

const BudgetPacingProgressBar = ({
  value,
  goal,
  pacing,
  variant = 'default',
  children,
  ...rest
}: BudgetPacingProgressBarProps) => {
  const pct = useMemo(() => calculatePercentage(value, goal), [value, goal])

  const overpacingVisual =
    pacing === 'overpacing' && Number(value) > Number(goal) && Number(goal) > 0

  const darkSegmentPct = overpacingVisual
    ? Math.min(100, (Number(goal) * 100) / Number(value))
    : 0

  const showHeader = variant === 'default' && children !== undefined

  return (
    <S.Root {...rest} $compact={variant === 'compact'}>
      {showHeader && (
        <S.HeaderRow>
          <S.HeaderBody>{children}</S.HeaderBody>
          <S.IndicatorWrap $pacing={pacing}>
            <PacingGlyph pacing={pacing} />
          </S.IndicatorWrap>
        </S.HeaderRow>
      )}
      <S.BarTrack>
        {overpacingVisual ? (
          <>
            <S.BarFillOverBase />
            <S.BarFillOverDark $widthPct={darkSegmentPct} />
          </>
        ) : (
          <S.BarFillSingle $widthPct={pct} />
        )}
      </S.BarTrack>
    </S.Root>
  )
}

export default BudgetPacingProgressBar
